function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

// Modifies a css value string to pixels (just number)
function computePixelValue(calcValue, parent = document.body) {
  const tempElement = document.createElement('div');
  tempElement.style.position = 'absolute';
  tempElement.style.visibility = 'hidden';
  tempElement.style.height = calcValue;
  parent.appendChild(tempElement);

  const pixels = window.getComputedStyle(tempElement).height;
  parent.removeChild(tempElement);

  return parseFloat(pixels);
}


class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling?.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    if (!summary.classList.contains('accordion__content'))
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (!elementToFocus) return;

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function measureAccordionHeights(items) {
  const states = Array.from(items, (item) => ({ item, content: item.querySelector('summary ~ *'), wasOpen: item.open }));
  states.forEach(({ item }) => { item.open = true; });
  states.forEach((state) => { state.height = state.content.getBoundingClientRect().height; });
  states.forEach(({ item, height, wasOpen }) => {
    item.style.setProperty('--accordion-content-height', `${height}px`);
    item.dataset.contentHeight = height;
    if (!wasOpen) item.open = false;
    setTimeout(() => item.classList.add('loaded'), 0);
  });
}

function closingAccordions(detailsClass = '.accordion details') {
  const accordionsDetails = document.querySelectorAll(detailsClass);

  measureAccordionHeights(accordionsDetails);

  accordionsDetails.forEach((item) => {
    if (!item.hasAttribute('listened')) {
      item.setAttribute('listened', true);
      item.querySelector('summary').addEventListener('click', (e) => {
        e.preventDefault();
        if (item.hasAttribute('open')) {
          item.classList.add('closing');
          e.currentTarget.setAttribute('aria-expanded', 'true');
          setTimeout(() => {
            item.removeAttribute('open');
            item.classList.remove('closing');
          }, 305);
        } else {
          item.setAttribute('open', '');
          item.classList.add('closing');
          e.currentTarget.setAttribute('aria-expanded', 'true');
          setTimeout(() => {
            item.classList.remove('closing');
          });
        }
      });
    }

    const contentUpdateObserver = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.type === 'childList') {
          item.classList.remove('loaded');
          measureAccordionHeights([item]);
        }
      }
    });

    contentUpdateObserver.observe(item, { childList: true, subtree: true });
  });

  let prevWindowWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === prevWindowWidth) return;
    prevWindowWidth = window.innerWidth;
    accordionsDetails.forEach((item) => item.classList.remove('loaded'));
    measureAccordionHeights(accordionsDetails);
  });
}

function updateAccordionsHeight(detailElement) {
  detailElement.style.setProperty('--accordion-content-height', `100%`);
  setTimeout(() => {
    const content = detailElement.querySelector('summary ~ *');
    const contentHeight = content.getBoundingClientRect().height;
    detailElement.style.setProperty('--accordion-content-height', `${contentHeight}px`);
    detailElement.dataset.contentHeight = contentHeight;
  }, 306);
}

function updateAccordionsDetailsHeight(detailsClass = '.accordion details') {
  const accordionsDetails = document.querySelectorAll(detailsClass);
  accordionsDetails.forEach((item) => item.classList.remove('loaded'));
  measureAccordionHeights(accordionsDetails);
}

closingAccordions();

function setStickyScrollUpFacets() {
  const stickyIntersects = document.querySelectorAll('.facets-sticky-intersect');
  if (stickyIntersects.length <= 0) return;

  // Check if facet is floating
  stickyIntersects.forEach((inter) => {
    const facet = inter.parentElement.querySelector('.facets-wrapper--sticky');
    if (!facet) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          facet.classList.add('floating');
        } else {
          facet.classList.remove('floating');
        }
      },
      { threshold: [0], rootMargin: '-1px 0px 0px 0px' }
    );

    observer.observe(inter);
  });

  let currentScroll = window.scrollY;
  const stickyFacets = document.querySelectorAll('.facets-wrapper--sticky--scroll');
  if (stickyFacets.length <= 0) return;

  const facetHeights = new Map();
  const facetObservers = [];
  const updateFacetHeight = (facet) => { facetHeights.set(facet, facet.getBoundingClientRect().height); };
  stickyFacets.forEach((facet) => {
    updateFacetHeight(facet);
    facet.style.setProperty('--facet-height', `${facetHeights.get(facet)}px`);
    const ro = new ResizeObserver(() => {
      updateFacetHeight(facet);
      facet.style.setProperty('--facet-height', `${facetHeights.get(facet)}px`);
    });
    ro.observe(facet);
    facetObservers.push(ro);
  });

  window.addEventListener('scroll', () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    stickyFacets.forEach((facet) => {
      if (scrollTop > currentScroll && facet.classList.contains('floating')) {
        facet.classList.add('scroll-hidden');
      } else {
        facet.classList.remove('scroll-hidden');
      }
    });

    currentScroll = scrollTop;
  });
}

setStickyScrollUpFacets();

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"getPlayerState","args":""}', '*');
    window.addEventListener('message', (event) => {
      if (event.data === 1) {
        video.setAttribute('data-was-playing', 'true');
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      }
    });
  });

  document.querySelectorAll('.js-vimeo').forEach((video) => {
    if (typeof Vimeo == 'undefined') return;
    const player = new Vimeo.Player(video);
    if (!player) return;
    player.getPaused().then((paused) => {
      if (!paused) {
        video.setAttribute('data-was-playing', 'true');
        player.pause();
      }
    });
  });

  document.querySelectorAll('video').forEach((video) => {
    const shouldIgnore = video.closest('[data-do-not-pause]') !== null;
    if (shouldIgnore) return;

    if (!video.paused) {
      video.setAttribute('data-was-playing', 'true');
      video.pause();
    }
  });

  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI && model.modelViewerUI.playing) {
      model.setAttribute('data-was-playing', 'true');
      model.modelViewerUI.pause();
    }
  });
}

function replayAllMedia() {
  document.querySelectorAll('.js-youtube[data-was-playing="true"]').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    video.removeAttribute('data-was-playing');
  });

  document.querySelectorAll('.js-vimeo[data-was-playing="true"]').forEach((video) => {
    if (typeof Vimeo == 'undefined') return;
    const player = new Vimeo.Player(video);
    if (!player) return;
    player.play();
    video.removeAttribute('data-was-playing');
  });

  document.querySelectorAll('video[data-was-playing="true"]').forEach((video) => {
    video.play();
    video.removeAttribute('data-was-playing');
  });

  document.querySelectorAll('product-model[data-was-playing="true"]').forEach((model) => {
    if (model.modelViewerUI) {
      model.modelViewerUI.play();
      model.removeAttribute('data-was-playing');
    }
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

function setAnnouncementBarHeight() {
  const announcementBar = document.querySelector('.utility-bar');
  if (announcementBar) {
    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      `${announcementBar.getBoundingClientRect().height}px`
    );
  } else {
    document.documentElement.style.setProperty('--announcement-bar-height', `0px`);
  }
}

function setUptBarsHeight() {
  setAnnouncementBarHeight();
  window.addEventListener('resize', () => {
    setAnnouncementBarHeight();
  });
}
setUptBarsHeight();

function announcementBarCloseButton() {
  document.querySelectorAll('.utility-bar[data-id]').forEach((bar) => {
    const sectionId = bar.dataset.id;
    const isClosed = sessionStorage.getItem(`announcement-closed-${sectionId}`) === 'true';

    if (isClosed) {
      bar.classList.add('hidden');
    }
    setAnnouncementBarHeight();
  });

  document.querySelectorAll('[data-close-button]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const sectionId = button.dataset.sectionId;
      const utilityBar = document.querySelector(`.utility-bar[data-id="${sectionId}"]`);

      if (utilityBar) {
        utilityBar.classList.add('hidden');
        setAnnouncementBarHeight();
        sessionStorage.setItem(`announcement-closed-${sectionId}`, 'true');
      }
    });
  });
}

announcementBarCloseButton();

function setUpResponsiveVideo() {
  const videos = document.querySelectorAll('.responsive-video video');

  if (videos.lenght <= 0) {
    return;
  }

   const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        // Only act if video is displayed (has size)
        const isVisibleInLayout = video.offsetWidth > 0 && video.offsetHeight > 0;

        if (!isVisibleInLayout) {
          return; // skip invisible (e.g. display: none or wrong media query)
        }

        if (entry.isIntersecting) {
          if (video.hasAttribute('data-was-playing')) {
            video.removeAttribute('data-was-playing');
            video.play();
          }
        } else {
          if (!video.paused) {
            video.setAttribute('data-was-playing', 'true');
            video.pause();
          }
        }
      });
    },
    {
      threshold: 0.01,
    }
  );

  videos.forEach((video) => {
    observer.observe(video);
  });
}
setUpResponsiveVideo();

function setUpUnloadedVideo() {
  const videos = document.querySelectorAll('video.unloaded-video');

  if (videos.length <= 0) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        const isVisibleInLayout = video.offsetWidth > 0 && video.offsetHeight > 0;

        if (!isVisibleInLayout) {
          return;
        }

        if (entry.isIntersecting) {
          if (!video.classList.contains('loaded')) {
            const sourcesTemplate = document.querySelector(`template#${video.dataset.sources}`);
            const clone = sourcesTemplate.content.cloneNode(true);
            const sources = clone.querySelectorAll('source');
            sources.forEach(source => {
              video.appendChild(source);
            });
            requestAnimationFrame(() => {
              video.addEventListener('canplay', () => video.play().catch(() => {}), { once: true });
              video.load();
              video.classList.add('loaded');
            });
          }
        }
      });
    },
    {
      threshold: 0.01,
    }
  );

  videos.forEach((video) => {
    observer.observe(video);
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('video.unloaded-video.loaded').forEach((video) => {
      video.play().catch(() => {});
    });
  });
}
setUpUnloadedVideo();

function setUpResponsiveExternalVideos() {
  const videos = document.querySelectorAll('.responsive-external-video .js-youtube, .responsive-external-video .js-vimeo');
  if (videos.lenght <= 0) {
    return;
  }

  videos.forEach(video => {
    video.addEventListener('load', () => {
      video.contentWindow.postMessage(
        JSON.stringify({ event: 'listening' }),
        '*'
      );
    });
  });

  const ytState = new WeakMap();

  window.addEventListener('message', e => {
    if (!e.data) return;
    let msg;
    try { msg = JSON.parse(e.data); } catch { return; }
    if (msg.event === 'infoDelivery' && msg.info?.playerState != null) {
      const iframe = Array.from(videos).find(v => v.contentWindow === e.source);
      if (iframe) {
        ytState.set(iframe, msg.info.playerState);
        // console.log('YT state for', iframe, msg.info.playerState);
      }
    }
  });

  const playVideo = (video) => {
    if (video.hasAttribute('data-was-playing')) {
      if (video.classList.contains('js-youtube')) {
        video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        video.removeAttribute('data-was-playing');
      }

      if (video.classList.contains('js-vimeo')) {
        if (typeof Vimeo == 'undefined') return;
        const player = new Vimeo.Player(video);
        if (!player) return;
        player.play();
        video.removeAttribute('data-was-playing');
      }
    }

  }

  const pauseVideo = (video) => {
     if (video.classList.contains('js-youtube')) {
      const state = ytState.get(video);
      if (state === 1) { // was playing
        video.setAttribute('data-was-playing', 'true');
        video.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          '*'
        );
      }
    }

    if (video.classList.contains('js-vimeo')) {
      if (typeof Vimeo == 'undefined') return;
      const player = new Vimeo.Player(video);
      if (!player) return;
      player.getPaused().then((paused) => {
        if (!paused) {
          video.setAttribute('data-was-playing', 'true');
          player.pause();
        }
      });
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        // Only act if video is displayed (has size)
        const isVisibleInLayout =
          video.offsetWidth > 0 && video.offsetHeight > 0;

        if (!isVisibleInLayout) {
          return; // skip invisible (e.g. display: none or wrong media query)
        }

        if (entry.isIntersecting) {
          playVideo(video);
        } else {
          pauseVideo(video);
        }
      });
    },
    {
      threshold: 0.01,
    }
  );

  videos.forEach((video) => {
    observer.observe(video);
  });
}

function renderExternalVideosScripts() {
  if (document.querySelector('.js-vimeo')) {
    const ogVimeoScript = document.querySelector('script[src="https://player.vimeo.com/api/player.js"]');
    if (!ogVimeoScript) {
      const vimeoScript = document.createElement('script');
      vimeoScript.src ="https://player.vimeo.com/api/player.js";
      vimeoScript.defer = 'defer'

      document.body.appendChild(vimeoScript);
    }
  }
  if (document.querySelector('.js-youtube')) {
    const ogYoutubeScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (!ogYoutubeScript) {
      const youtubeScript = document.createElement('script');
      youtubeScript.src ="https://www.youtube.com/iframe_api";

      document.body.appendChild(youtubeScript)
    }
  }

  setTimeout(() => {
    setUpResponsiveExternalVideos();
  }, 100)
}
renderExternalVideosScripts();

function setUpResponsiveImages() {
  const images = document.querySelectorAll('img.responsive-image');

  const resizeImage = (image) => {
    image.setAttribute('sizes', `${Math.ceil(image.getBoundingClientRect().width)}px`);
  };

  images.forEach((image) => {
    resizeImage(image);
  });

  window.addEventListener('resize', () => {
    images.forEach((image) => {
      resizeImage(image);
    });
  });
}
setUpResponsiveImages();

/* Mega Menu with Layers events */
function toggleActiveClass(element, className = 'active') {
  element.classList.toggle(className);
}

function removeActiveClassFromElements(elements, className = 'active') {
  elements.forEach((element) => element.classList.remove(className));
}

function handleFirstLevelClick(event) {
  const firstLevelElement = event.currentTarget;
  const firstLevelElementId = firstLevelElement.id;
  const megaMenuLayersWrappers = document.querySelectorAll('.mega-menu__content-wrapper');

  megaMenuLayersWrappers.forEach((wrapper) => {
    const contentBlocks = wrapper.querySelectorAll('.header-content-block');
    contentBlocks.forEach((block) => {
      if (block.dataset.referenceId === firstLevelElementId) {
        block.classList.add('active');
        wrapper.classList.remove('active');

        const thirdLevelActive = wrapper.querySelectorAll('.mega-menu__content-third-level .active');
        removeActiveClassFromElements(thirdLevelActive);
      } else {
        block.classList.remove('active');
      }
    });
  });
}

function handleSecondLevelClick(event) {
  const secondLevelElement = event.currentTarget;
  const secondLevelElementId = secondLevelElement.id;
  const parentElementDataset = secondLevelElement.dataset.parent;
  const megaMenuLayersWrappers = document.querySelectorAll('.mega-menu__content-wrapper');

  megaMenuLayersWrappers.forEach((wrapper) => {
    const contentBlocks = wrapper.querySelectorAll('.header-content-block');
    if (contentBlocks.length > 0) {
      contentBlocks.forEach((block) => {
        if (block.dataset.referenceId === secondLevelElementId) {
          toggleActiveClass(block);
        } else {
          block.classList.remove('active');
        }
      });
    }

    const parentContentBlocks = wrapper.querySelectorAll(
      `.header-content-block[data-reference-id="${parentElementDataset}"]`
    );
    const thirdLevelSubmenus = wrapper.querySelectorAll('.mega-menu__content-third-level ul');

    thirdLevelSubmenus.forEach((submenu) => {
      if (submenu.dataset.referenceId === secondLevelElementId) {
        toggleActiveClass(submenu);
        if (!submenu.classList.contains('active') && parentContentBlocks.length > 0) {
          parentContentBlocks.forEach((parentBlock) => parentBlock.classList.add('active'));
        }
      } else {
        submenu.classList.remove('active');
      }
    });

    const thirdLevelSubmenusActive = wrapper.querySelectorAll('.mega-menu__content-third-level ul.active');
    wrapper.classList.toggle('active', thirdLevelSubmenusActive.length > 0);
  });
}

function setupMegaMenuLayersEvents() {
  const headerInlineMenu = document.querySelector('.header__inline-menu');
  if (headerInlineMenu.classList.contains('mega-menu-layers')) {
    const firstLevelItems = headerInlineMenu.querySelectorAll('.header__menu-item');
    const parentHasMenuHover = headerInlineMenu.querySelectorAll('.menu-hover');
    firstLevelItems.forEach((item) => {
      if (parentHasMenuHover.length > 0) {
        item.addEventListener('mouseover', handleFirstLevelClick);
      } else {
        item.addEventListener('click', handleFirstLevelClick);
      }
    });

    const secondLevelItems = headerInlineMenu.querySelectorAll('.menu-item--second-level__span');
    secondLevelItems.forEach((item) => item.addEventListener('click', handleSecondLevelClick));
  }
}
if (document.querySelector('.header')) setupMegaMenuLayersEvents();

/* END - Mega Menu with Layers events */
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.otherVariantSelects = Array.from(document.querySelectorAll('quantity-input')).filter((element) => {
      return (element.querySelector('input').id == this.input.id && element != this)
    });

    this.otherVariantSelects.forEach(otherInput => {
      otherInput.querySelector('input').value = this.input.value;
    })
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
        this.otherVariantSelects.forEach(otherInput => {
          otherInput.input.stepUp();
        })

      }
    } else {
      this.input.stepDown();
      this.otherVariantSelects.forEach(otherInput => {
        otherInput.input.stepDown();
      })
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
      this.otherVariantSelects.forEach(otherInput => {
        otherInput.querySelector(".quantity__button[name='minus']").classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
      })
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
      this.otherVariantSelects.forEach(otherInput => {
        otherInput.querySelector(".quantity__button[name='plus']").classList.toggle('disabled', value >= max);
      })
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.isFacetDrawerAccordionSetup = false;
    this.firstLevelDetails = this.querySelectorAll('.menu-drawer__first-level-details');
    this.bindOnSummaryClick = this.onSummaryClick.bind(this);

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => summary.addEventListener('click', this.bindOnSummaryClick));
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.parentElement?.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      this.firstLevelDetails.forEach((detail) => {
        detail.querySelector('summary').disabled = true;
      });
      if (
        this.hasAttribute('carousel-drawer') &&
        detailsElement.classList.contains('menu-drawer__first-level-details')
      ) {
        event.preventDefault();

        this.firstLevelDetails.forEach((detail) => {
          if (detail != detailsElement) {
            detail.querySelector('summary').classList.remove('selected');
            this.closeSubmenu(detail, false, 300);
          }
        });

        if (!isOpen) {
          event.preventDefault();
          detailsElement.setAttribute('open', true);
          summaryElement.setAttribute('aria-expanded', true);
          summaryElement.classList.add('selected');
          parentMenuElement && parentMenuElement.classList.add('submenu-open');
          requestAnimationFrame(() => {
            detailsElement.classList.add('menu-opening');
          });
        }
      } else {
        setTimeout(() => {
          detailsElement.classList.add('menu-opening');
          summaryElement.setAttribute('aria-expanded', true);
          parentMenuElement && parentMenuElement.classList.add('submenu-open');
          !reducedMotion || reducedMotion.matches
            ? addTrapFocus()
            : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
        }, 100);
      }
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    if (this.isFacetDrawerAccordionSetup == false && this.classList.contains('mobile-facets__wrapper')) {
      setTimeout(() => {
        closingAccordions('.mobile-facets__dropdown details');
        this.isFacetDrawerAccordionSetup = true;
      }, 210);
    }
    if (this.isFacetDrawerAccordionSetup == true && this.classList.contains('mobile-facets__wrapper')) {
      setTimeout(() => {
        updateAccordionsDetailsHeight('.mobile-facets__dropdown details');
      }, 210);
    }
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    setTimeout(() => {
      const mediaQuery = this.getCurrentMediaQuery();
      document.body.classList.add(`overflow-hidden${mediaQuery}`, 'overflow-hidden-drawer');
    }, 100);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      if (this.hasAttribute('carousel-drawer') && details.classList.contains('menu-drawer__first-level-details'))
        return;
      if (this.hasAttribute('carousel-drawer')) {
        setTimeout(() => {
          if (this.hasAttribute('dropdown-menu')) {
            details.querySelector('summary').click();
          } else {
            this.closeSubmenu(details);
          }
        }, 300);
      } else {
        if (!details.classList.contains('facets__open')) {
          details.removeAttribute('open');
        }
        details.classList.remove('menu-opening');
      }
    });

    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      if (this.hasAttribute('carousel-drawer') && submenu.classList.contains('menu-drawer__first-level-details'))
        return;
      submenu.classList.remove('submenu-open');
    });

    this.removeOverflowHidden();
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement, closeParent = true, delay = 400) {
    const parentMenuElement = detailsElement.parentElement?.closest('.has-submenu');

    if (closeParent) {
      parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    }

    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement, delay);
  }

  closeAnimation(detailsElement, delay = 400) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < delay) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        if (!detailsElement.classList.contains('facets__open')) {
          detailsElement.removeAttribute('open');
        }
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }

        if (this.hasAttribute('carousel-drawer')) {
          detailsElement.querySelectorAll('details[open]').forEach((details) => {
            if (details.classList.contains('menu-drawer__first-level-details')) return;

            if (this.hasAttribute('dropdown-menu')) {
              details.querySelector('summary').click();
            } else {
              this.closeSubmenu(details);
            }
          });
        }

        this.firstLevelDetails.forEach((detail) => {
          detail.querySelector('summary').disabled = false;
        });
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  getCurrentMediaQuery() {
    // Add modify in case the menu drawer breackpoint changes
    let mediaQuery = '';
    if (window.innerWidth < 990) {
      mediaQuery = '-drawer';
    }

    return mediaQuery;
  }

  removeOverflowHidden() {
    document.body.classList.forEach((c) => {
      if (c.includes('overflow-hidden')) document.body.classList.remove(c);
    });
    document.body.classList.remove('overflow-hidden-drawer');
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.isDropdownDrawerAnimationSetup = false;
    this.isFirstLevelOnOpenSetup = false;
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;

    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );

    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    if (this.isDropdownDrawerAnimationSetup == false && this.hasAttribute('dropdown-menu')) {
      this.firstLevelDetails.forEach((firstLevel) => {
        firstLevel.menuprevopen = firstLevel.open == true;

        setTimeout(() => (firstLevel.open = true));
      });
      setTimeout(() => {
        closingAccordions('.menu-drawer__second-level-details');
        this.firstLevelDetails.forEach((firstLevel) => {
          firstLevel.querySelectorAll('.menu-drawer__second-level-details').forEach((secondLevel) => {
            secondLevel.addEventListener('click', () => {
              updateAccordionsHeight(firstLevel);
            });
          });
          if (firstLevel.menuprevopen == false) {
            firstLevel.open = false;
          }

          firstLevel.menuprevopen = false;
        });
      });
      setTimeout(() => {
        if (!this.hasAttribute('carousel-drawer')) {
          closingAccordions('.menu-drawer__first-level-details');
        }
      });

      this.isDropdownDrawerAnimationSetup = true;
    }

    if (!this.isFirstLevelOnOpenSetup && this.hasAttribute('carousel-drawer')) {
      requestAnimationFrame(() => {
        this.firstLevelEventsAdded = false;
        this.carouselContainer = this.querySelector('.drawer--carousel');
        if (this.carouselContainer) {
          this.carouselIsDown = false;
          this.carouselStartX = 0;
          this.carouselScrollLeft = 0;
          this.bindCarouselMouseDown = this.carouselMouseDown.bind(this);
          this.bindCarouselMouseMove = this.carouselMouseMove.bind(this);
          this.bindCarouselMouseUp = this.carouselMouseUp.bind(this);
          this.setUpCarouselSlider();
          window.addEventListener('resize', debounce(this.setUpCarouselSlider.bind(this), 200), { passive: true });
          this.isFirstLevelOnOpenSetup = true;
        }
      });
    }

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    const mediaQuery = this.getCurrentMediaQuery();
    document.body.classList.add(`overflow-hidden${mediaQuery}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };

  carouselMouseDown(event) {
    event.preventDefault();
    this.carouselContainer.classList.add('grabbing');
    this.carouselIsDown = true;
    this.carouselStartX = event.pageX - this.carouselContainer.offsetLeft;
    this.carouselScrollLeft = this.carouselContainer.scrollLeft;

    this.carouselContainer.addEventListener('mousemove', this.bindCarouselMouseMove, { passive: false });
    this.carouselContainer.addEventListener('mouseup', this.bindCarouselMouseUp, { passive: false });
    this.carouselContainer.addEventListener('mouseleave', this.bindCarouselMouseUp, { passive: false });
  }

  carouselMouseMove(event) {
    event.preventDefault();
    if (!this.carouselIsDown) return;
    this.carouselContainer.classList.add('moving');
    const x = event.pageX - this.carouselContainer.offsetLeft;
    const walk = x - this.carouselStartX;
    this.carouselContainer.scrollLeft = this.carouselScrollLeft - walk;
  }

  carouselMouseUp() {
    this.carouselIsDown = false;
    this.carouselContainer.classList.remove('moving');
    this.carouselContainer.classList.remove('grabbing');

    this.carouselContainer.removeEventListener('mousemove', this.bindCarouselMouseMove, { passive: false });
    this.carouselContainer.removeEventListener('mouseup', this.bindCarouselMouseUp, { passive: false });
    this.carouselContainer.removeEventListener('mouseleave', this.bindCarouselMouseUp, { passive: false });
  }

  setUpCarouselSlider() {
    if (!this.carouselContainer) return;
    this.carouselContainer.style.setProperty('--carousel-height', `${this.carouselContainer.clientHeight}px`);

    if (this.carouselContainer.scrollWidth > this.carouselContainer.clientWidth) {
      this.carouselContainer.classList.add('grab');
      this.carouselContainer.addEventListener('mousedown', this.bindCarouselMouseDown, { passive: false });
      this.firstLevelEventsAdded = true;
    } else {
      this.carouselContainer.classList.remove('grab');
      this.carouselContainer.removeEventListener('mousedown', this.bindCarouselMouseDown, { passive: false });
      this.firstLevelEventsAdded = false;
    }
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.setOverflowHidden = true;
    this.hideDelay = 505;
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model, img, component-slider')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (this.setOverflowHidden) if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    if (this.setOverflowHidden) document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    this.classList.add('closed');
    setTimeout(() => {
      this.removeAttribute('open');
      if (this.setOverflowHidden)
        if (
          !document.body.querySelector('.header__search-predictive-search details[open]') &&
          !document.body.querySelector('quick-add-modal[open]') &&
          !document.body.querySelector('modal-dialog[open]')
        )
          document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      removeTrapFocus(this.openedBy);
      window.replayAllMedia();
      this.classList.remove('closed')
    }, this.hideDelay)
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');

    if (!this.button) return;
    this.button.addEventListener('click', () => {
      this.openModal(this.button);
    });
  }

  openModal(trigger) {
    const modal = document.querySelector(this.getAttribute('data-modal'));
    if (modal) modal.show(trigger || this.button);
  }
}
customElements.define('modal-opener', ModalOpener);
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (this.hasAttribute('autoplay')) {
      this.loadContent();
      return;
    }
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    } else {
      this.play();
    }
  }

  play() {
    const deferredElement = this.querySelector('video, model-viewer, iframe');
    if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
      deferredElement.play();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.setUpSliderComponent();
  }

  setUpSliderComponent() {
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    if (this.hasAttribute('mouse-slider')) {
      this.setUpMouseDrag();
      this.slider.addEventListener('scrollend', this.onScrollEnd.bind(this));
    }

    if (this.classList.contains('hide-tablet-slider')) {
      this.hideTabletSlider();
      window.addEventListener('resize', this.hideTabletSlider.bind(this));
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.sliderItemOffsetVertical = this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.hasAttribute('vertical-slider')) {
      if (this.isSlideVisibleVertical(this.sliderItemsToShow[0]) || this.slider.scrollTop <= 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (
        this.isSlideVisibleVertical(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]) ||
        this.slider.scrollTop >= this.slider.scrollHeight - this.slider.clientHeight
      ) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    } else {
      if (this.isSlideVisible(this.sliderItemsToShow[0]) || this.slider.scrollLeft <= 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }
      if (
        this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]) ||
        this.slider.scrollLeft >= this.slider.scrollWidth - this.slider.clientWidth
      ) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }
  isSlideVisibleVertical(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientHeight + this.slider.scrollTop - offset;
    return element.offsetTop + element.clientHeight <= lastVisibleSlide && element.offsetTop >= this.slider.scrollTop;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;

    if (this.hasAttribute('vertical-slider')) {
      this.slideScrollPosition =
        event.currentTarget.name === 'next'
          ? this.slider.scrollTop + step * this.sliderItemOffsetVertical
          : this.slider.scrollTop - step * this.sliderItemOffsetVertical;
      this.setSlidePosition(this.slideScrollPosition);
    } else {
      this.slideScrollPosition =
        event.currentTarget.name === 'next'
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
      this.setSlidePosition(this.slideScrollPosition);
    }
  }

  setSlidePosition(position) {
    const behavior = this.dataset.scrollBehavior ? this.dataset.scrollBehavior : 'smooth';
    if (this.hasAttribute('vertical-slider')) {
      this.slider.scrollTo({
        top: position,
        behavior: behavior,
      });
    } else {
      this.slider.scrollTo({
        left: position,
        behavior: behavior,
      });
    }
  }

  setUpMouseDrag() {
    let isDragging = false;
    let startX, startY;
    let startScrollLeft, startScrollTop;
    let deltaX = 0;
    let deltaY = 0;
    this.slider.style.cursor = 'grab';

    this.slider.addEventListener('mousedown', (e) => {
      if (e.target.closest('card-media-gallery')) return;
      this.slider.style.cursor = 'grabbing';

      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = this.slider.scrollLeft;
      startScrollTop = this.slider.scrollTop;

      document.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    });

    const onMouseMove = (e) => {
      e.preventDefault();
      if (!isDragging) return;
      this.sliderItems.forEach((item) => {
        item.style.pointerEvents = 'none';
      });

      if (this.hasAttribute('vertical-slider')) {
        deltaY = e.clientY - startY;
        this.slider.scrollTop = startScrollTop - deltaY;
      } else {
        deltaX = e.clientX - startX;
        this.slider.scrollLeft = startScrollLeft - deltaX;
      }
    };

    const onMouseUp = () => {
      isDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      this.slider.style.cursor = 'grab';
      this.sliderItems.forEach((item) => {
        item.style.pointerEvents = 'all';
      });
      if (window.innerWidth > 750)
        if (this.hasAttribute('snap-on-slide')) {
          this.snapProductGallery();
        }
    };

    this.slider.addEventListener('mouseleave', () => {
      onMouseUp();
    });

    if (this.hasAttribute('snap-on-slide')) {
      this.slider.addEventListener('touchend', () => {
        onMouseUp();
      });
    }
  }

  onScrollEnd() {
    let scrollTimeout;
    const _this = this;
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(function () {
      if (_this.hasAttribute('snap-on-slide')) {
        _this.snapProductGallery();
      }
    }, 100);
  }

  snapProductGallery() {
    this.sliderItemsToShow.forEach((item, index) => {
      const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;
      let conditionalLeft =
        item.offsetLeft <= this.slider.scrollLeft && item.offsetLeft + item.clientWidth > this.slider.scrollLeft;
      if (index == 0) {
        conditionalLeft =
          item.offsetLeft <= this.slider.scrollLeft &&
          item.offsetLeft + item.clientWidth * 0.5 > this.slider.scrollLeft;
      } else if (index > 0) {
        conditionalLeft =
          item.offsetLeft - this.sliderItemsToShow[index - 1].clientWidth * 0.5 <= this.slider.scrollLeft &&
          item.offsetLeft + item.clientWidth * 0.5 > this.slider.scrollLeft;
      }

      if (index == this.sliderItemsToShow.length - 1) {
        if (this.slider.scrollLeft >= maxScrollLeft - item.clientWidth * 0.4) {
          conditionalLeft = true;
        }
      }

      if (this.slider.scrollLeft <= this.sliderItemsToShow[0].clientWidth / 2) {
        if (index == 0) {
          conditionalLeft = true;
        } else {
          conditionalLeft = false;
        }
      }

      if (conditionalLeft) {
        this.slider.scroll({
          left: item.offsetLeft,
          behavior: 'smooth',
        });
      }
    });
  }

  hideTabletSlider() {
    if (window.innerWidth >= 750 && window.innerWidth < 990) {
      this.classList.add('page-width');
      this.slider.classList.remove('slider--tablet', 'grid--peek');
    } else {
      this.classList.remove('page-width');
      this.slider.classList.add('slider--tablet', 'grid--peek');
    }
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.otherVariantSelects = Array.from(document.querySelectorAll(`variant-selects[id^="${this.id}"]`)).filter(
      (selector) => {
        return selector != this;
      }
    );

    if (this.otherVariantSelects.length > 0) {
      this.currentScroll = 0;
      this.onScroll();
      window.addEventListener('scroll', this.onScroll.bind(this));
      this.querySelectorAll('input').forEach((elem) => {
        elem.addEventListener('focus', (e) => {
          window.scrollTo({ top: this.currentScroll });
        });
      });
    }

    this.stickyBars = document.querySelectorAll('.product-sticky-bar');
    this.inComponentElement = this.dataset.inComponentSelector != null ? this.closest(this.dataset.inComponentSelector) : null;
    this.bindInputClick = this.onComponentInputClick.bind(this);
    this.bindDirectInputClick = this.onDirectInputClick.bind(this);

    if (this.inComponentElement) {
      const availableInputs = this.querySelectorAll('input');
      availableInputs.forEach((inpt) => {
        if (!inpt.hasAttribute('unavailable')) {
          inpt.addEventListener('click', this.bindDirectInputClick);
        }
      });
    }
  }

  onScroll() {
    this.currentScroll = window.pageYOffset || document.documentElement.scrollTop;
  }

  onVariantChange(event) {
    if (this.selectedUnavailable) this.selectedUnavailable.removeEventListener('click', this.bindInputClick);
    if (typeof window._RestockRocket == 'undefined' && this.inComponentElement) {
      this.inComponentElement.removeAttribute('notify-on-variant');
    }
    if (this.inComponentElement) {
      if (this.inComponentElement.hasAttribute('notify-on-variant') && event.target.hasAttribute('unavailable')) {
        event.preventDefault();
        this.options = Array.from(
          this.querySelectorAll('select.product-form__select, fieldset.product-form__input'),
          (element) => {
            if (element.tagName === 'SELECT') {
              return element.value;
            }
            if (element.tagName === 'FIELDSET') {
              return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
            }
          }
        );

        this.selectedUnavailable = event.target;
        if (this.selectedUnavailable) this.selectedUnavailable.addEventListener('click', this.bindInputClick);

        this.updateMasterId();
        if (this.dataset.inComponentSelector == 'card-quick-add') {
          const quickAdd = this.closest('card-quick-add');
          if (quickAdd) quickAdd.triggerRestockModal()
        } else {
          this.triggerRestockModal(event.target);
        }
        return;
      } else if(this.inComponentElement.hasAttribute('direct-add') && !event.target.hasAttribute('unavailable')) {
        event.preventDefault();
        return;
      }

    }

    this.updateOptions();
    this.updateMasterId();
    this.updateSelectedSwatchValue(event);
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, window.variantStrings.unavailable, true);
      this.setUnavailable();
    } else {
      this.setButtonsAwaitClass(true);
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }

    this.updateOther();
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll('select.product-form__select, fieldset.product-form__input'),
      (element) => {
        if (element.tagName === 'SELECT') {
          return element.value;
        }
        if (element.tagName === 'FIELDSET') {
          return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
        }
      }
    );

    Array.from(this.querySelectorAll('select.product-form__select, fieldset.product-form__input')).forEach(
      (element, index) => {
        if (element.tagName === 'SELECT') {
          element.value = this.options[index];
        }
        if (element.tagName === 'FIELDSET') {
          Array.from(element.querySelectorAll('input')).forEach((input) => {
            if (input.value == this.options[index]) input.classList.add('current');
            else input.classList.remove('current');
          });
        }
      }
    );
  }

  updateOther() {
    this.otherVariantSelects.forEach((other) => {
      const otherOptions = other.querySelectorAll('select.product-form__select, fieldset.product-form__input');
      otherOptions.forEach((element, index) => {
        if (element.tagName === 'SELECT') {
          element.value = this.options[index];
        }
        if (element.tagName === 'FIELDSET') {
          Array.from(element.querySelectorAll('input')).forEach((input) => {
            if (input.value == this.options[index]) input.classList.add('current');
            else input.classList.remove('current');
          });
        }
      });
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });

    const productInfo = this.closest('product-info');

    if (productInfo && this.currentVariant) {
      productInfo.dataset.selectedVariant = this.currentVariant.id;
      const restockButton = productInfo.querySelector('.restock-custom-trigger');
      if (restockButton) restockButton.dataset.variantId = this.currentVariant.id;
    }
  }

  updateSelectedSwatchValue({ target }) {
    const { name, value, tagName } = target;
    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = this.querySelector(`[data-selected-dropdown-swatch="${name}"] > .swatch`);
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = this.querySelector(`[data-selected-swatch-value="${name}"]`);
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
    this.updateOtherSelectedSwatchValue(target);

    this.stickyBars.forEach(bar => {
      bar.updateSwatches(target);
    })
  }

  updateOtherSelectedSwatchValue(target) {
    const { name, value, tagName } = target;

    this.otherVariantSelects.forEach((other) => {
      if (tagName === 'SELECT' && target.selectedOptions.length) {
        const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
        const selectedDropdownSwatchValue = other.querySelector(`[data-selected-dropdown-swatch="${name}"] > .swatch`);
        if (!selectedDropdownSwatchValue) return;
        if (swatchValue) {
          selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
          selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
        } else {
          selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
          selectedDropdownSwatchValue.classList.add('swatch--unavailable');
        }

        selectedDropdownSwatchValue.style.setProperty(
          '--swatch-focal-point',
          target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
        );
      } else if (tagName === 'INPUT' && target.type === 'radio') {
        const selectedSwatchValue = other.querySelector(`[data-selected-swatch-value="${name}"]`);
        const swatchValue = target.dataset.swatch;
        if (selectedSwatchValue){
          if (swatchValue) {
            const selectedSwatchValueSwatch = other.querySelector(`[data-selected-swatch-value="${name}"] .swatch`);
            selectedSwatchValueSwatch.style.setProperty('--swatch--background', swatchValue);
          } else {
            selectedSwatchValue.innerHTML = value;
          }
        }
      }
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    // if (!this.currentVariant.featured_media) return;
    const featuredMediaId = this.currentVariant.featured_media
      ? this.currentVariant.featured_media.id
      : this.dataset.defaultMediaId;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);

    mediaGalleries.forEach((mediaGallery) =>
      mediaGallery.setActiveMedia(`${this.dataset.section}-${featuredMediaId}`, true, false)
    );

    const productModal = document.querySelector(`#ProductModal-${this.dataset.section}`);
    if (!productModal) return;
    productModal.setActiveVariantMedia(`${featuredMediaId}`, true, false);

  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    // window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    const url = new URL(window.location.href);
    const params = url.searchParams;
    params.set('variant', `${this.currentVariant.id}`);
    url.search = params.toString();
    window.history.replaceState({}, '', url);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #${this.dataset.productFormId}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });

    this.otherVariantSelects.forEach(vs => {
      const vsInputWrappers = [...vs.querySelectorAll('.product-form__input')]
      vsInputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
    })
  }

  setInputAvailability(elementList, availableValuesList) {
    elementList.forEach((element) => {
      const value = element.getAttribute('value');
      const availableElement = availableValuesList.includes(value);

      if (element.tagName === 'INPUT') {
        element.classList.toggle('disabled', !availableElement);
      } else if (element.tagName === 'OPTION') {
        element.innerText = availableElement
          ? value
          : window.variantStrings.unavailable_with_option.replace('[value]', value);
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability || !this.currentVariant) return;

    pickUpAvailability.fetchAvailability(this.currentVariant.id);
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    const renderFetchUrl =
      this.dataset.templateView == undefined
        ? `${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`
        : `${this.dataset.url}?variant=${requestedVariantId}&view=${this.dataset.templateView}`;

    fetch(renderFetchUrl)
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destinations = Array.from(
          document.querySelectorAll(
            `#price-${this.dataset.section}, #action-bar-price-${this.dataset.section}, #sticky-bar-price-${this.dataset.section}`
          )
        );
        const source = html.getElementById(`price-${sectionId}`);
        const skuSource = html.getElementById(`Sku-${sectionId}`);
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(`Inventory-${sectionId}`);
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        const volumePricingSource = html.getElementById(`Volume-${sectionId}`);

        const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const pricePerItemSource = html.getElementById(`Price-Per-Item-${sectionId}`);

        const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
        const qtyRulesArray = document.querySelectorAll(
          `#Quantity-Rules-${this.dataset.section}, #BuyButtons-Quantity-Rules-${this.dataset.section}, #BuyButtons-ActionBar-Quantity-Rules-${this.dataset.section}`
        );
        const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

        if (volumeNote) volumeNote.classList.remove('hidden');
        if (volumePricingDestination) volumePricingDestination.classList.remove('hidden');
        qtyRulesArray.forEach((qtyRules) => {
          if (qtyRules) qtyRules.classList.remove('hidden');
        });

        destinations.forEach((destination) => {
          if (source && destination) destination.innerHTML = source.innerHTML;
        });

        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        if (volumePricingSource && volumePricingDestination) {
          volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
        }

        if (pricePerItemSource && pricePerItemDestination) {
          pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
          pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList.contains('hidden'));
        }

        const prices = Array.from(
          document.querySelectorAll(
            `#price-${this.dataset.section}, #action-bar-price-${this.dataset.section}, #sticky-bar-price-${this.dataset.section}`
          )
        );
        prices.forEach((price) => {
          if (price) price.classList.remove('hidden');
        });

        if (document.querySelector('product-sticky-bar') && html.querySelector('product-sticky-bar')) {
          document.querySelector('.product-sticky-bar__info__image').innerHTML = html.querySelector(
            '.product-sticky-bar__info__image'
          ).innerHTML;
        }

        const customSelectors = [`#ProductBadges-${this.dataset.section}`, `#DetailsProductAccordion-${this.dataset.section} .accordion__content`, `#ProductSchema`];
        customSelectors.forEach((selector) => {
          const selectorSource = html.querySelector(selector);
          const selectorDestination = document.querySelector(selector);

          if (selectorSource && selectorDestination) {
            selectorDestination.innerHTML = selectorSource.innerHTML;
          }
        });

        // Alta Joyeria Form
        const altaJoyeriaFormDestination = document.querySelector(`#AltaJoyeriaForm-${this.dataset.section}`);
        const altaJoyeriaFormSource = html.querySelector(`#AltaJoyeriaForm-${this.dataset.section}`);
        if (altaJoyeriaFormDestination && altaJoyeriaFormSource) {
          const inputAJUpdateArray = ['contact[product_url]', 'contact[SKU]', 'contact[variant_selected]', 'contact[Variant ID]'];
          inputAJUpdateArray.forEach(input_name => {
            const inputAJDestination = altaJoyeriaFormDestination.querySelector(`input[name="${input_name}"]`);
            const inputAJSource = altaJoyeriaFormSource.querySelector(`input[name="${input_name}"]`);

            if (inputAJDestination && inputAJSource) {
              inputAJDestination.value = inputAJSource.value;
            }
          })
        }

        // Store Stock modal
        const storeStockDestinations = document.querySelectorAll(`store-stock`);
        const storeStockSource = html.querySelector(`store-stock`);

        if (storeStockSource && storeStockDestinations.length > 0) {
          storeStockDestinations.forEach(dest => {
            dest.dataset.reference = storeStockSource.dataset.reference;
            dest.dataset.productId = storeStockSource.dataset.productId;

            // Update selected options subtitle
            const selectedOptionsSource = storeStockSource.querySelector('[data-selected-options]');
            const selectedOptionsDest = dest.querySelector('[data-selected-options]');
            if (selectedOptionsSource && selectedOptionsDest) {
              selectedOptionsDest.textContent = selectedOptionsSource.textContent;
            }

            requestAnimationFrame(() => {
              dest.reset();
            })
          })
        }

        if (inventoryDestination) inventoryDestination.classList.toggle('hidden', inventorySource.innerText === '');

        const addButtonUpdated =
          this.dataset.templateView != undefined
            ? html.querySelector('[id^="ProductSubmitButton-"]')
            : html.getElementById(`ProductSubmitButton-${sectionId}`);

        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.variantStrings.soldOut
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });

        this.setButtonsAwaitClass(false);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(this.dataset.productFormId);
    if (!productForm) return;

    const buttonId = productForm.querySelector('[name="add"][id^="ProductSubmitButton"]').id;
    const addButtons = document.querySelectorAll(`[name="add"][id="${buttonId}"]`);
    if (addButtons.length <= 0) return;

    addButtons.forEach((addButton, index) => {
      if (!addButton) return;
      addButton.classList.remove('change-wait');
      const addButtonText = addButton.querySelector('span');
      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        addButton.setAttribute('aria-disabled', 'true');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButton.removeAttribute('aria-disabled');
        addButtonText.textContent = window.variantStrings.addToCart;
      }
    });

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(this.dataset.productFormId);
    const productForm = document.getElementById(this.dataset.productFormId);
    const buttonId = productForm.querySelector('[name="add"][id^="ProductSubmitButton"]').id;
    const addButtons = document.querySelectorAll(`[name="add"][id="${buttonId}"]`);
    const addButtonTexts = document.querySelectorAll(`[name="add"][id="${buttonId}"] > span`);
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
    const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);
    const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
    const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

    if (addButtons.length <= 0) return;
    addButtons.forEach((addButton, index) => {
      if (!addButton) return;
      addButtonTexts[index].textContent = window.variantStrings.unavailable;
    });
    if (price) price.classList.add('hidden');
    if (inventory) inventory.classList.add('hidden');
    if (sku) sku.classList.add('hidden');
    if (pricePerItem) pricePerItem.classList.add('hidden');
    if (volumeNote) volumeNote.classList.add('hidden');
    if (volumeTable) volumeTable.classList.add('hidden');
    if (qtyRules) qtyRules.classList.add('hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  setButtonsAwaitClass(awaiting) {
    const productForm = document.getElementById(this.dataset.productFormId);
    if (!productForm) return;

    const buttonId = productForm.querySelector('[name="add"][id^="ProductSubmitButton"]').id;
    const addButtons = document.querySelectorAll(`[name="add"][id="${buttonId}"]`);
    if (addButtons.length <= 0) return;

    addButtons.forEach((button) => {
      button
        .closest('product-form')
        .querySelector('.product-form__error-message-wrapper')
        ?.setAttribute('hidden', true);
      if (awaiting) {
        button.classList.add('awaiting-change');
      } else {
        button.classList.remove('awaiting-change');
      }
    });
  }

  triggerRestockModal() {
    this.restockJson = document.querySelector('.main-product-restock-json');
    if (this.restockJson) this.restockProductInfo = JSON.parse(this.restockJson.textContent);

    const productInfo = this.closest('product-info');

    if (!this.restockProductInfo || !productInfo || typeof window._RestockRocket == 'undefined') return;
    window._RestockRocket.openModal(this.restockProductInfo, +productInfo.dataset.variantId);
  }

  onComponentInputClick() {
    if (this.dataset.inComponentSelector == 'card-quick-add') {
      const quickAdd = this.closest('card-quick-add');
      if (quickAdd) quickAdd.triggerRestockModal()
    } else {
      this.triggerRestockModal(event.target);
    }
  }

  onDirectInputClick(event) {
    if(this.inComponentElement.hasAttribute('direct-add')) {
      this.options = Array.from(
        this.querySelectorAll('select.product-form__select, fieldset.product-form__input'),
        (element) => {
          if (element.tagName === 'SELECT') {
            return element.value;
          }
          if (element.tagName === 'FIELDSET') {
            return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
          }
        }
      );
      this.updateMasterId();
      this.updateVariantInput();

      const productForm = document.getElementById(this.dataset.productFormId);
      if (productForm) {
        const submitButton = productForm.querySelector('[name="add"]');

        if (submitButton) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-disabled');
          submitButton.click();
        }
      }
    }


  }
}

customElements.define('variant-selects', VariantSelects);

function customRestockAction(event) {
  const restockJson = document.querySelector('.main-product-restock-json');
  let restockProductInfo = null
  if (restockJson) restockProductInfo = JSON.parse(restockJson.textContent);

  if (typeof window._RestockRocket != 'undefined' && restockProductInfo != null) {
    const element = event.currentTarget;
    if (!element) return;
    window._RestockRocket.openModal(restockProductInfo, +element.dataset.variantId);
  }

}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.loadRecomendations();
  }

  loadRecomendations() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations && recommendations.innerHTML.trim().length) {
          HTMLUpdateUtility.setInnerHTML(this, recommendations.innerHTML);
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
